import { useState, useEffect, useRef } from "react";
import "./App.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileCode,
  faLaptopCode,
  faBroom,
  faWandMagicSparkles,
} from "@fortawesome/free-solid-svg-icons";


import Header from "./Header";
import Footer from "./Footer";
import JsonViewer from "./JsonViewer";
import TransformBuilder from "./components/TransformBuilder"; // ⬅️ NEW

// utils (re-exported from src/utils/index.js)
import { flattenJson, buildCombinedCode } from "./utils";

function App() {
  const [rawText, setRawText] = useState("");
  const [userInput, setUserInput] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [resultArray, setResultArray] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResult, setSearchResult] = useState([]);
  const [copiedKey, setCopiedKey] = useState("");
  const [copiedType, setCopiedType] = useState("");
  const [bulkCopyTick, setBulkCopyTick] = useState(false);

  const outPut = useRef([]);
  const rootPath = "$document";

  /* ============= JSON INPUT HANDLERS ============= */
  const handleChange = (e) => {
    const value = e.target.value;
    setRawText(value);
    setErrorMessage("");

    try {
      const jsonObj = JSON.parse(value);
      setUserInput(jsonObj);
      outPut.current = flattenJson(jsonObj, rootPath);
      setResultArray(outPut.current);
      setSearchResult(outPut.current);
    } catch (err) {
      setUserInput(null);
      setResultArray([]);
      setSearchResult([]);
      setErrorMessage("Invalid JSON: " + err.message);
    }
  };

  const prettify = () => {
    try {
      const obj = JSON.parse(rawText);
      setRawText(JSON.stringify(obj, null, 4));
      setErrorMessage("");
    } catch (e) {
      setErrorMessage("Invalid JSON: " + e.message);
    }
  };

  const minify = () => {
    try {
      const obj = JSON.parse(rawText);
      setRawText(JSON.stringify(obj));
      setErrorMessage("");
    } catch (e) {
      setErrorMessage("Invalid JSON: " + e.message);
    }
  };

  const clearAll = () => {
    setRawText("");
    setUserInput(null);
    setErrorMessage("");
    setResultArray([]);
    setSearchResult([]);
    setSearchTerm("");
  };

  /* ============= SEARCH HANDLER ============= */
  useEffect(() => {
    if (!searchTerm) {
      setSearchResult(resultArray);
      return;
    }
    const timer = setTimeout(() => {
      const filtered = resultArray.filter((s) =>
        s.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setSearchResult(filtered);
    }, 220);
    return () => clearTimeout(timer);
  }, [searchTerm, resultArray]);

  /* ============= COPY LOGIC ============= */
  const hasJson = !!(userInput && Object.keys(userInput).length > 0);

  const copyAllPaths = () => {
    const source = searchTerm ? searchResult : resultArray;
    const lines = (source || [])
      .map((s) => s.split(":")[0])
      .filter((p) => p && p !== "null");
    if (!lines.length) return;
    navigator.clipboard.writeText(lines.join("\n"));
    setBulkCopyTick(true);
  };

  useEffect(() => {
    if (!copiedKey) return;
    const t = setTimeout(() => {
      setCopiedKey("");
      setCopiedType("");
    }, 1600);
    return () => clearTimeout(t);
  }, [copiedKey]);

  useEffect(() => {
    if (!bulkCopyTick) return;
    const t = setTimeout(() => setBulkCopyTick(false), 1600);
    return () => clearTimeout(t);
  }, [bulkCopyTick]);

  /* ============= JSX UI ============= */
  return (
    <>
      <Header />
      <main className="container">
        {/* JSON Input Card */}
        <section className="card inputCard">
          <div className="cardHeader">
            <h2>JSON Input</h2>
            <div className="toolbar">
              <button className="pillBtn" onClick={prettify}>
                <FontAwesomeIcon icon={faWandMagicSparkles} /> Prettify
              </button>
              <button className="pillBtn" onClick={minify}>
                <FontAwesomeIcon icon={faWandMagicSparkles} /> Minify
              </button>
              <button className="pillBtn" onClick={clearAll}>
                <FontAwesomeIcon icon={faBroom} /> Clear
              </button>
            </div>
          </div>

          <textarea
            className={`jsonTextarea ${errorMessage ? "error" : ""}`}
            rows={18}
            value={rawText}
            onChange={handleChange}
            placeholder="Paste or type your JSON here"
          />
          {errorMessage && <div className="errorMsg">{errorMessage}</div>}
        </section>

        {/* Search + Results Card */}
        <section className="card resultsCard">
          <div className="cardHeader">
            <h2>Search</h2>
            <div className="toolbar">
              <button className="pillBtn" onClick={copyAllPaths}>
                <FontAwesomeIcon icon={faFileCode} /> Copy All Paths
              </button>
              {bulkCopyTick && <span className="copyTick">All paths copied ✓</span>}
            </div>
          </div>

          <input
            className="searchInput"
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search key or value to find the path"
          />

          <div className="resultList">
            {!searchResult?.length && hasJson && (
              <div className="emptyState">No matches found.</div>
            )}
            {searchResult?.length > 0 && (
              <ol className="rows">
                {searchResult.map((item) => {
                  const path = item.split(":")[0];
                  const value = item.substring(item.indexOf(":") + 1);
                  const isCopied = copiedKey === item;
                  return (
                    <li className="row" key={item}>
                      <div className="path" title={path}>
                        {path}
                      </div>
                      <div className="value" title={value}>
                        {value}
                      </div>
                      <div className="actions">
                        <button
                          className="iconBtn"
                          title="Copy JSONPath"
                          onClick={() => {
                            setCopiedKey(item);
                            setCopiedType("path");
                            navigator.clipboard.writeText(path);
                          }}
                        >
                          <FontAwesomeIcon icon={faFileCode} />
                        </button>
                        <button
                          className="iconBtn"
                          title="Copy Velocity + Python code"
                          onClick={() => {
                            setCopiedKey(item);
                            setCopiedType("velocity");
                            navigator.clipboard.writeText(
                              buildCombinedCode(path)
                            );
                          }}
                        >
                          <FontAwesomeIcon icon={faLaptopCode} />
                        </button>
                        {isCopied && (
                          <span className="copyTick">
                            {copiedType === "path"
                              ? "Path copied ✓"
                              : "Velocity+Python copied ✓"}
                          </span>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
          </div>
        </section>

        {/* JSON Viewer Card */}
        {hasJson && (
          <section className="card viewerCard">
            <div className="cardHeader">
              <h2>JSON Viewer</h2>
            </div>
            <JsonViewer inputjson={userInput} />
          </section>
        )}

        {/* Transformation Builder Card (NEW) */}
        {hasJson && (
          <TransformBuilder flattenedPaths={resultArray} />
        )}
      </main>
      <Footer />
    </>
  );
}

export default App;
